import { Box } from '@mui/material';
import React, { useId, useState } from 'react';
import { KitButton, KitCard, KitList, KitPill, SecH } from '../common/MobileKit';
import { KitSelect } from '../common/MobileKitExtras';
import { MobileListRow } from '../common/MobileCrud';
import { BarsChart, DonutCard, HBars, KitEmptyNote, Legend } from '../common/MobileDashboardKit';

/**
 * Peças do Relatório de Vendas no shell mobile (lista de vendas e gráficos extras).
 * Só apresentação: os dados e cálculos vêm de `pages/reports/SalesReport.js`, no mesmo
 * estilo dos kits (`MobileDashboardKit`, `MobileCrud`).
 */

// Cartão de gráfico: título (14.5/800) + subtítulo opcional + ação à direita (ex.: seletor de ano).
export function ChartCard({ title, subtitle, action, bare, children }) {
    return (
        <>
            <SecH
                title={title}
                sx={subtitle || action ? { mb: subtitle ? '2px' : '10px' } : undefined}
            />
            {(subtitle || action) && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mb: '10px' }}>
                    <Box sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.4, minWidth: 0 }}>{subtitle}</Box>
                    {action}
                </Box>
            )}
            {bare ? children : <KitCard>{children}</KitCard>}
        </>
    );
}

// Seletor de ano compacto (36px de altura, 16px de fonte para o iOS não dar zoom).
export function YearSelect({ value, onChange, options, label }) {
    return (
        <KitSelect
            aria-label={label}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            sx={{ width: 96, height: 36, borderRadius: '11px', flexShrink: 0, fontWeight: 700, backgroundPosition: 'right 10px center', pr: '30px', pl: '12px' }}
        >
            {options.map((y) => <option key={y} value={y}>{y}</option>)}
        </KitSelect>
    );
}

export function SeriesLegend({ items }) {
    return (
        <Box sx={{ display: 'flex', gap: '16px', mt: '10px', flexWrap: 'wrap' }}>
            <Legend items={items} />
        </Box>
    );
}

// Linha com área (série temporal). `items`: [{ label, value }]. Rótulos do eixo X espaçados para caber em 390px.
export function AreaLine({ items, maxLabels = 6 }) {
    const gid = useId().replace(/:/g, '');
    const n = items.length;
    const max = Math.max(1, ...items.map((i) => i.value || 0));
    const xs = (i) => (n === 1 ? 50 : (i / (n - 1)) * 100);
    const ys = (v) => 100 - ((v || 0) / max) * 92 - 4;
    const line = items.map((it, i) => `${xs(i)},${ys(it.value)}`).join(' ');
    const area = `0,100 ${line} 100,100`;
    const step = Math.max(1, Math.ceil(n / maxLabels));
    return (
        <Box>
            <Box sx={{ position: 'relative', height: 130 }}>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
                    {[0, 1, 2].map((k) => <Box key={k} sx={{ borderTop: '1px solid', borderColor: 'divider' }} />)}
                </Box>
                <Box component="svg" viewBox="0 0 100 100" preserveAspectRatio="none" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                    <defs>
                        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.35" />
                            <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <polygon points={area} fill={`url(#${gid})`} />
                    <polyline points={line} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                </Box>
            </Box>
            <Box sx={{ position: 'relative', height: 18, mt: '6px' }}>
                {items.map((it, i) => (i % step === 0 ? (
                    <Box
                        key={`${it.label}-${i}`}
                        component="span"
                        sx={{
                            position: 'absolute',
                            left: `${xs(i)}%`,
                            transform: i === 0 && n > 1 ? 'none' : i === n - 1 && n > 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                            fontSize: 10.5,
                            color: 'text.disabled',
                            fontWeight: 600,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {it.label}
                    </Box>
                ) : null))}
            </Box>
        </Box>
    );
}

export function Caption({ children }) {
    return <Box sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4, mt: '10px' }}>{children}</Box>;
}

// Barras horizontais com "Ver mais" quando a lista é longa (categorias/subcategorias).
export function HBarsLimited({ items, limit = 8, valueWidth }) {
    const [all, setAll] = useState(false);
    const shown = all ? items : items.slice(0, limit);
    return (
        <>
            <HBars items={shown} valueWidth={valueWidth} />
            {items.length > limit && (
                <KitButton block sx={{ mt: '6px', width: '100%' }} onClick={() => setAll((v) => !v)}>
                    {all ? 'Ver menos' : `Ver todos (${items.length})`}
                </KitButton>
            )}
        </>
    );
}

export function DonutBlock({ segments, center, legend, empty }) {
    if (!segments.some((s) => s.value > 0)) return <KitCard><KitEmptyNote>{empty}</KitEmptyNote></KitCard>;
    return <DonutCard segments={segments} center={center} legend={legend} />;
}

export { BarsChart };

const statusTone = (status) => {
    const key = (status || '').toLowerCase();
    if (key === 'finalizada') return 'success';
    if (key === 'cancelada' || key === 'estornada') return 'danger';
    if (key === 'iniciada') return 'info';
    return 'warning';
};

/**
 * Lista de vendas (`.list` com `MobileListRow`), com "Carregar mais" (20 por vez).
 * - rows: vendas já filtradas/ordenadas
 * - getRow(row) -> { number, customer, date, payments, total, status }
 * - onOpen(row) abre a visualização da venda
 */
export function SalesList({ rows, getRow, onOpen, pageSize = 20 }) {
    const [visible, setVisible] = useState(pageSize);
    if (rows.length === 0) return <KitCard><KitEmptyNote>Nenhuma venda no período</KitEmptyNote></KitCard>;
    const shown = rows.slice(0, visible);
    return (
        <>
            <KitList>
                {shown.map((row, index) => {
                    const r = getRow(row);
                    return (
                        <MobileListRow
                            key={`${row.id ?? r.number}-${index}`}
                            title={`#${r.number || '—'} · ${r.customer || 'Sem cliente'}`}
                            sub={[r.date, r.payments].filter(Boolean).join(' · ')}
                            end={(
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    <Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>{r.total}</Box>
                                    <KitPill tone={statusTone(r.status)}>{r.status || '—'}</KitPill>
                                </Box>
                            )}
                            chevron
                            onClick={() => onOpen(row)}
                        />
                    );
                })}
            </KitList>
            <Box sx={{ textAlign: 'center', fontSize: 12, color: 'text.disabled', m: '10px 0 6px', lineHeight: 1.4 }}>
                Exibindo {shown.length} de {rows.length} vendas
            </Box>
            {visible < rows.length && (
                <KitButton block sx={{ width: '100%' }} onClick={() => setVisible((v) => v + pageSize)}>Carregar mais</KitButton>
            )}
        </>
    );
}
